import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Search, Plus, Edit, Trash2, Mail, MessageSquare, Key, Loader2, TestTube, Star } from 'lucide-react';
import { apiService, HookProvider, CreateHookProviderRequest } from '../../services/api';

export function HookProvidersPage() {
  const [providers, setProviders] = useState<HookProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | { message?: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'EMAIL' | 'SMS'>('ALL');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<HookProvider | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'email' as 'email' | 'sms',
    isDefault: false,
    emailConfig: {
      host: '',
      port: 587,
      secure: false,
      user: '',
      password: '',
      from: '',
      replyTo: ''
    },
    smsConfig: {
      provider: '',
      apiKey: '',
      gateway: '',
      originator: ''
    }
  });

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getHookProviders();
      setProviders(response.items || []);
    } catch (err) {
      console.error('Failed to load hook providers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load hook providers');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    console.log(`${type.toUpperCase()}: ${message}`);
  };

  const filteredProviders = (providers || []).filter(provider => {
    const matchesSearch = (provider.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (provider.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'ALL' || provider.type.toUpperCase() === typeFilter;
    return matchesSearch && matchesType;
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'email',
      isDefault: false,
      emailConfig: {
        host: '',
        port: 587,
        secure: false,
        user: '',
        password: '',
        from: '',
        replyTo: ''
      },
      smsConfig: {
        provider: '',
        apiKey: '',
        gateway: '',
        originator: ''
      }
    });
  };

  const handleCreate = () => {
    setEditingProvider(null);
    resetForm();
    setIsDialogOpen(true);
  };

  const handleEdit = (provider: HookProvider) => {
    setEditingProvider(provider);

    setFormData({
      name: provider.name,
      description: provider.description || '',
      type: provider.type,
      isDefault: provider.isDefault,
      emailConfig: provider.type === 'email' && provider.config.smtp ? {
        host: provider.config.smtp.host,
        port: provider.config.smtp.port,
        secure: provider.config.smtp.secure || false,
        user: provider.config.smtp.auth.user,
        password: '••••••••', // Mask existing password
        from: provider.config.smtp.from,
        replyTo: provider.config.smtp.replyTo || ''
      } : {
        host: '',
        port: 587,
        secure: false,
        user: '',
        password: '',
        from: '',
        replyTo: ''
      },
      smsConfig: provider.type === 'sms' && provider.config.sms ? {
        provider: provider.config.provider || '',
        apiKey: '••••••••', // Mask existing API key
        gateway: provider.config.sms.gateway || '',
        originator: provider.config.sms.originator || ''
      } : {
        provider: '',
        apiKey: '',
        gateway: '',
        originator: ''
      }
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      showNotification('Name ist erforderlich', 'error');
      return;
    }

    try {
      setSaving(true);

      const requestData: CreateHookProviderRequest = {
        name: formData.name,
        description: formData.description,
        type: formData.type === 'email' ? 'email' : 'sms',
        isDefault: formData.isDefault,
        config: formData.type === 'email' ? {
          smtp: {
            host: formData.emailConfig.host,
            port: formData.emailConfig.port,
            secure: formData.emailConfig.secure,
            auth: {
              user: formData.emailConfig.user,
              pass: formData.emailConfig.password === '••••••••' && editingProvider ? (editingProvider.config as any).smtp?.auth?.pass || '' : formData.emailConfig.password
            },
            from: formData.emailConfig.from,
            replyTo: formData.emailConfig.replyTo || undefined,
            tls: { rejectUnauthorized: !formData.emailConfig.secure }
          }
        } : {
          provider: formData.smsConfig.provider || 'default',
          sms: {
            apiKey: formData.smsConfig.apiKey === '••••••••' && editingProvider ? (editingProvider.config as any).sms?.apiKey || '' : formData.smsConfig.apiKey,
            gateway: formData.smsConfig.gateway,
            originator: formData.smsConfig.originator
          }
        }
      };

      if (editingProvider) {
        const updatedProvider = await apiService.updateHookProvider(editingProvider.id, requestData);
        setProviders(providers.map(p => p.id === editingProvider.id ? updatedProvider : p));
        showNotification('Hook Provider erfolgreich aktualisiert');
      } else {
        const newProvider = await apiService.createHookProvider(requestData);
        setProviders([...providers, newProvider]);
        showNotification('Hook Provider erfolgreich erstellt');
      }

      setIsDialogOpen(false);
      setEditingProvider(null);
      resetForm();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      showNotification(`Fehler beim Speichern: ${errorMessage}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const provider = providers.find(p => p.id === id);
    if (!provider) return;

    if (confirm(`Sind Sie sicher, dass Sie den Hook Provider "${provider.name}" löschen möchten?`)) {
      try {
        await apiService.deleteHookProvider(id);
        setProviders(providers.filter(p => p.id !== id));
        showNotification('Hook Provider erfolgreich gelöscht');
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
        showNotification(`Fehler beim Löschen: ${errorMessage}`, 'error');
      }
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      const testData = {
        type: formData.type,
        config: formData.type === 'email' ? {
          smtp: {
            host: formData.emailConfig.host,
            port: formData.emailConfig.port,
            secure: formData.emailConfig.secure,
            auth: {
              user: formData.emailConfig.user,
              pass: formData.emailConfig.password
            },
            from: formData.emailConfig.from,
            replyTo: formData.emailConfig.replyTo
          }
        } : {
          provider: formData.smsConfig.provider,
          sms: {
            apiKey: formData.smsConfig.apiKey,
            gateway: formData.smsConfig.gateway,
            originator: formData.smsConfig.originator
          }
        },
        recipient: formData.type === 'email' ? 'test@example.com' : '+491234567890',
        subject: formData.type === 'email' ? 'Test E-Mail' : undefined,
        content: 'Dies ist eine Test-Nachricht zur Überprüfung der Hook Provider Konfiguration.'
      };

      const result = await apiService.testHookProvider(testData);
      if (result.success) {
        showNotification('Test erfolgreich: Provider funktioniert korrekt');
      } else {
        showNotification(`Test fehlgeschlagen: ${result.message}`, 'error');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      showNotification(`Test-Fehler: ${errorMessage}`, 'error');
    } finally {
      setTesting(false);
    }
  };

  const toggleActive = async (id: string) => {
    const provider = providers.find(p => p.id === id);
    if (!provider) return;

    try {
      const newStatus = provider.status === 'active' ? 'inactive' : 'active';
      const updatedProvider = await apiService.updateHookProvider(id, { status: newStatus });
      setProviders(providers.map(p => p.id === id ? updatedProvider : p));
      showNotification(`Provider ${newStatus === 'active' ? 'aktiviert' : 'deaktiviert'}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      showNotification(`Fehler beim Ändern des Status: ${errorMessage}`, 'error');
    }
  };

  const setAsDefault = async (id: string, type: 'email' | 'sms') => {
    try {
      await apiService.setDefaultHookProvider(id, type);
      setProviders(providers.map(p => {
        if (p.type === type) {
          return { ...p, isDefault: p.id === id };
        }
        return p;
      }));
      showNotification(`Als Standard-${type}-Provider festgelegt`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      showNotification(`Fehler beim Setzen als Standard: ${errorMessage}`, 'error');
    }
  };


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hook Providers</h1>
          <p className="text-gray-600">Verwaltung vorkonfigurierter E-Mail und SMS Provider</p>
        </div>
        <Button onClick={handleCreate} disabled={loading}>
          <Plus className="w-4 h-4 mr-2" />
          Neuer Provider
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Provider Übersicht</CardTitle>
              <CardDescription>{providers?.length || 0} Provider konfiguriert</CardDescription>
            </div>
            <div className="flex space-x-4">
              <Select value={typeFilter} onValueChange={(value: 'ALL' | 'EMAIL' | 'SMS') => setTypeFilter(value)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Alle</SelectItem>
                  <SelectItem value="EMAIL">E-Mail</SelectItem>
                  <SelectItem value="SMS">SMS</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Provider suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Provider laden...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{typeof error === 'string' ? error : (error?.message || 'Ein Fehler ist aufgetreten')}</p>
              <Button onClick={loadProviders} variant="outline">
                Erneut versuchen
              </Button>
            </div>
          ) : filteredProviders.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm || typeFilter !== 'ALL' ? 'Keine Provider gefunden' : 'Noch keine Provider konfiguriert'}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredProviders.map((provider) => (
                <div key={provider.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        {provider.type === 'email' ? (
                          <Mail className="w-5 h-5 text-blue-500" />
                        ) : (
                          <MessageSquare className="w-5 h-5 text-green-500" />
                        )}
                        <h3 className="font-semibold text-gray-900">{provider.name}</h3>
                        <Badge variant={provider.status === 'active' ? 'default' : 'secondary'}>
                          {provider.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                        <Badge variant="outline" className={provider.type === 'email' ? 'text-blue-700 border-blue-200' : 'text-green-700 border-green-200'}>
                          {provider.type === 'email' ? 'EMAIL' : 'SMS'}
                        </Badge>
                        {provider.isDefault && (
                          <Badge variant="outline" className="text-yellow-700 border-yellow-200">
                            <Star className="w-3 h-3 mr-1" />
                            Standard
                          </Badge>
                        )}
                      </div>
                      <p className="text-gray-600 mb-2">{provider.description}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Key className="w-3 h-3" />
                          <span>Konfiguriert</span>
                        </div>
                        <div>Erstellt: {new Date(provider.createdAt).toLocaleDateString('de-DE')}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {!provider.isDefault && provider.status === 'active' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setAsDefault(provider.id, provider.type)}
                          title="Als Standard setzen"
                        >
                          <Star className="w-4 h-4" />
                        </Button>
                      )}
                      <Switch
                        checked={provider.status === 'active'}
                        onCheckedChange={() => toggleActive(provider.id)}
                      />
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(provider)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(provider.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {editingProvider ? 'Hook Provider bearbeiten' : 'Neuen Hook Provider erstellen'}
            </DialogTitle>
            <DialogDescription>
              Konfigurieren Sie einen E-Mail oder SMS Provider für Hook-Aktionen
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Provider Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="z.B. Firmen SMTP Server"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Beschreibung des Providers..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Provider Typ</Label>
              <Select value={formData.type} onValueChange={(value: 'email' | 'sms') => setFormData({...formData, type: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">E-Mail (SMTP)</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.type === 'email' && (
              <div className="space-y-4 border-t pt-4">
                <h4 className="font-medium">E-Mail Konfiguration</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="host">SMTP Host</Label>
                    <Input
                      id="host"
                      value={formData.emailConfig.host}
                      onChange={(e) => setFormData({...formData, emailConfig: {...formData.emailConfig, host: e.target.value}})}
                      placeholder="smtp.example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="port">Port</Label>
                    <Input
                      id="port"
                      type="number"
                      value={formData.emailConfig.port}
                      onChange={(e) => setFormData({...formData, emailConfig: {...formData.emailConfig, port: parseInt(e.target.value) || 587}})}
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="secure"
                    checked={formData.emailConfig.secure}
                    onCheckedChange={(checked) => setFormData({...formData, emailConfig: {...formData.emailConfig, secure: checked}})}
                  />
                  <Label htmlFor="secure">Sichere Verbindung (TLS/SSL)</Label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="user">Benutzername</Label>
                    <Input
                      id="user"
                      value={formData.emailConfig.user}
                      onChange={(e) => setFormData({...formData, emailConfig: {...formData.emailConfig, user: e.target.value}})}
                      placeholder="user@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Passwort</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.emailConfig.password}
                      onChange={(e) => setFormData({...formData, emailConfig: {...formData.emailConfig, password: e.target.value}})}
                      placeholder={editingProvider ? "Leer lassen um beizubehalten" : ""}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="from">Absender E-Mail</Label>
                    <Input
                      id="from"
                      value={formData.emailConfig.from}
                      onChange={(e) => setFormData({...formData, emailConfig: {...formData.emailConfig, from: e.target.value}})}
                      placeholder="noreply@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="replyTo">Antwort-Adresse (optional)</Label>
                    <Input
                      id="replyTo"
                      value={formData.emailConfig.replyTo}
                      onChange={(e) => setFormData({...formData, emailConfig: {...formData.emailConfig, replyTo: e.target.value}})}
                      placeholder="support@example.com"
                    />
                  </div>
                </div>
              </div>
            )}

            {formData.type === 'sms' && (
              <div className="space-y-4 border-t pt-4">
                <h4 className="font-medium">SMS Konfiguration</h4>
                <div className="space-y-2">
                  <Label htmlFor="provider">SMS Provider</Label>
                  <Input
                    id="provider"
                    value={formData.smsConfig.provider}
                    onChange={(e) => setFormData({...formData, smsConfig: {...formData.smsConfig, provider: e.target.value}})}
                    placeholder="z.B. Twilio, Nexmo, etc."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="apiKey">API Key</Label>
                  <Input
                    id="apiKey"
                    type="password"
                    value={formData.smsConfig.apiKey}
                    onChange={(e) => setFormData({...formData, smsConfig: {...formData.smsConfig, apiKey: e.target.value}})}
                    placeholder={editingProvider ? "Leer lassen um beizubehalten" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gateway">Gateway (optional)</Label>
                  <Input
                    id="gateway"
                    value={formData.smsConfig.gateway}
                    onChange={(e) => setFormData({...formData, smsConfig: {...formData.smsConfig, gateway: e.target.value}})}
                    placeholder="Gateway Identifier"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="originator">Originator (optional)</Label>
                  <Input
                    id="originator"
                    value={formData.smsConfig.originator}
                    onChange={(e) => setFormData({...formData, smsConfig: {...formData.smsConfig, originator: e.target.value}})}
                    placeholder="Sender ID / Phone Number"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Switch
                id="isDefault"
                checked={formData.isDefault}
                onCheckedChange={(checked) => setFormData({...formData, isDefault: checked})}
              />
              <Label htmlFor="isDefault">Als Standard-Provider setzen</Label>
            </div>

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={saving || testing || !formData.name}
              >
                {testing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Teste...
                  </>
                ) : (
                  <>
                    <TestTube className="w-4 h-4 mr-2" />
                    Test
                  </>
                )}
              </Button>

              <div className="flex space-x-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={saving}>
                  Abbrechen
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {editingProvider ? 'Speichern...' : 'Erstellen...'}
                    </>
                  ) : (
                    editingProvider ? 'Speichern' : 'Erstellen'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}